// What the Discard tool means on one square.
//
// The tool was Lift and knew one command, `remove-belt`; a building came down
// from a button in its inspector and nowhere else, so "get rid of this" had two
// doors depending on what "this" was. It is one tool now, and this is the
// question it asks of each square it touches.
//
// **A sweep never takes a building down, only a press does.** Lifting track is
// free and a drag crosses squares at pointer speed; `demolish` spends a piece's
// whole turn. A building under a sweep is passed over in silence, so the only
// way to spend the turn is to point at the thing and click it.

import type { Color } from "src/engine/rules/board";
import {
  beltOwnerAt,
  blueprintOwnerAt,
  type IndustryState,
  structureAt
} from "src/engine/rules/industry/state";

export type Discard =
  /** Bare ground, or a building merely swept across. Silent. */
  | { kind: "nothing" }
  /** Track, built or drawn. Anybody's — `removeBelt` says whose it is not. */
  | { kind: "part" }
  /** Your building, pressed. Costs the turn; `demolish` says what is missing. */
  | { kind: "structure" }
  | { kind: "refuse"; why: string };

export const DISCARD_NOT_YOURS = "That building is not yours to discard.";

export function discardAt(
  industry: IndustryState,
  square: number,
  side: Color,
  /** The pointer went down here, rather than being dragged across. */
  press: boolean
): Discard {
  const structure = structureAt(industry, square);
  if (structure) {
    if (!press) return { kind: "nothing" };
    if (structure.owner !== side) return { kind: "refuse", why: DISCARD_NOT_YOURS };
    return { kind: "structure" };
  }
  if (beltOwnerAt(industry, square) >= 0 || blueprintOwnerAt(industry, square) >= 0) {
    return { kind: "part" };
  }
  return { kind: "nothing" };
}
